import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './RichTextEditor.css';
import { Code2, Eye, FileText, PenLine } from 'lucide-react';

/** Isolated so a ticking clock does not re-render ReactQuill every second (that caused scroll jumps). */
function RteLiveClock() {
    const [now, setNow] = useState(() => new Date());

    useEffect(() => {
        const intervalId = window.setInterval(() => setNow(new Date()), 1000);
        return () => window.clearInterval(intervalId);
    }, []);

    return <span>Updated {now.toLocaleTimeString()}</span>;
}

export default function RichTextEditor({ initialContent = '', onChange }) {
    const quillRef = useRef(null);
    const [content, setContent] = useState(initialContent);
    const [wordCount, setWordCount] = useState(0);
    const [charCount, setCharCount] = useState(0);
    const [showSourceCode, setShowSourceCode] = useState(false);
    const [previewMode, setPreviewMode] = useState('editor'); // 'editor' or 'preview'
    const [hasUserEdited, setHasUserEdited] = useState(false);
    const lastInitialContentRef = useRef(initialContent);

    useEffect(() => {
        // If the loaded blog content changes (e.g. switching posts / async fetch),
        // reset the editor. During normal typing, the parent echoes the same HTML
        // back down so this will not run.
        if (lastInitialContentRef.current === initialContent) return;
        lastInitialContentRef.current = initialContent;
        setHasUserEdited(false);
        setContent(initialContent || '');
    }, [initialContent]);

    useEffect(() => {
        const editor = quillRef.current?.getEditor?.();
        if (!editor) return;

        const text = (editor.getText() || '').trim();
        const words = text ? text.split(/\s+/).length : 0;
        setWordCount(words);
        setCharCount(text.length);
    }, [content]);

    const modules = useMemo(
        () => ({
            table: true,
            toolbar: {
                container: [
                    [{ header: [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ color: [] }, { background: [] }],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    [{ align: [] }],
                    ['link', 'blockquote', 'code-block'],
                    ['insertTable'],
                    ['clean']
                ],
                handlers: {
                    insertTable: () => {
                        const editor = quillRef.current?.getEditor();
                        if (!editor) return;

                        const tableModule = editor.getModule('table');
                        if (tableModule && typeof tableModule.insertTable === 'function') {
                            tableModule.insertTable(3, 3);
                            return;
                        }

                        const range = editor.getSelection(true);
                        const insertAt = range ? range.index : editor.getLength();
                        const tableHtml =
                            '<table style="border-collapse: collapse; width: 100%; margin: 8px 0;">' +
                            '<tbody>' +
                            '<tr><td style="border:1px solid #cbd5e1;padding:8px;">Cell</td><td style="border:1px solid #cbd5e1;padding:8px;">Cell</td></tr>' +
                            '<tr><td style="border:1px solid #cbd5e1;padding:8px;">Cell</td><td style="border:1px solid #cbd5e1;padding:8px;">Cell</td></tr>' +
                            '</tbody></table><p><br/></p>';

                        editor.clipboard.dangerouslyPasteHTML(insertAt, tableHtml, 'user');
                    }
                }
            }
        }),
        []
    );

    const formats = [
        'header',
        'bold',
        'italic',
        'underline',
        'strike',
        'color',
        'background',
        'list',
        'bullet',
        'align',
        'link',
        'blockquote',
        'code-block',
        'table',
        'table-row',
        'table-cell'
    ];

    const handleEditorChange = (html, _delta, source, editor) => {
        // Keep Quill's raw HTML in state so typing (especially spaces) behaves correctly.
        setContent(html);
        if (source === 'user') setHasUserEdited(true);
        // IMPORTANT: send raw HTML upward; normalize at save-time (storage), not on each keystroke.
        if (onChange) onChange(html);

        const text = (editor.getText() || '').trim();
        const words = text ? text.split(/\s+/).length : 0;
        setWordCount(words);
        setCharCount(text.length);
    };

    return (
        <div className="rte-container">
            <div className="rte-max-width">
                <div className="rte-shell">
                    <header className="rte-section-head">
                        <div className="rte-section-head-icon" aria-hidden>
                            <FileText size={22} strokeWidth={2} />
                        </div>
                        <div className="rte-section-head-copy">
                            <p className="rte-section-eyebrow">Body</p>
                            <h2 className="rte-section-title">Content editor</h2>
                            <p className="rte-section-lede">
                                Formatting, tables, images, and HTML source when you need it.
                            </p>
                        </div>
                    </header>
                    <div className="rte-main-container">
                        <div className="rte-toolbar-section">

                            <button
                                type="button"
                                onClick={() =>
                                    setPreviewMode((prev) => (prev === 'editor' ? 'preview' : 'editor'))
                                }
                                className={`rte-button ${previewMode === 'preview' ? 'active' : ''}`}
                            >
                                {previewMode === 'editor' ? (
                                    <Eye size={15} strokeWidth={2} aria-hidden />
                                ) : (
                                    <PenLine size={15} strokeWidth={2} aria-hidden />
                                )}
                                <span>{previewMode === 'editor' ? 'Preview' : 'Editor'}</span>
                            </button>

                            {previewMode === "preview" && (
                                <button
                                    type="button"
                                    className={`rte-button ${showSourceCode ? 'active' : ''}`}
                                    onClick={() => setShowSourceCode((prev) => !prev)}
                                >
                                    {showSourceCode ? (
                                        <Eye size={15} strokeWidth={2} aria-hidden />
                                    ) : (
                                        <Code2 size={15} strokeWidth={2} aria-hidden />
                                    )}
                                    <span>{showSourceCode ? 'Preview HTML' : 'HTML source'}</span>
                                </button>
                            )}
                        </div>

                        <div className="rte-editor-container">
                            {previewMode === 'editor' && (
                                <div className="rte-editor-col">
                                    <label className="rte-col-label">Editor</label>
                                    <ReactQuill
                                        ref={quillRef}
                                        theme="snow"
                                        value={content}
                                        onChange={handleEditorChange}
                                        modules={modules}
                                        formats={formats}
                                        className="rte-quill"
                                    />
                                </div>
                            )}

                            {previewMode === 'preview' && (
                                <div className="rte-editor-col">
                                    <label className="rte-col-label">{showSourceCode ? 'HTML Source' : 'Preview'}</label>
                                    {showSourceCode ? (
                                        <textarea
                                            value={content}
                                            readOnly
                                            className="rte-source-code"
                                        />
                                    ) : (
                                        <div className="rte-preview ql-snow">
                                            <div
                                                className="ql-editor rte-preview-content"
                                                dangerouslySetInnerHTML={{ __html: content }}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="rte-status-bar">
                            <div className="rte-stat-item">
                                <span>Words</span>
                                <span className="rte-stat-value">{wordCount}</span>
                            </div>
                            <div className="rte-stat-item">
                                <span>Characters</span>
                                <span className="rte-stat-value">{charCount}</span>
                            </div>
                            <div className="rte-stat-item rte-stat-item--clock">
                                <RteLiveClock />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}